/**
 * Booking form time maths. The form has one date plus start/end times; an end at or before the
 * start means the meeting runs past midnight (e.g. 23:30 → 00:30). With the 4h cap that is the
 * only sensible reading, so the end rolls to the next day rather than being rejected.
 */
import { MAX_BOOKING_DURATION_HOURS, MIN_BOOKING_DURATION_MINUTES, REMIND_BEFORE_OPTIONS } from '../../../../types/RoomBooking'

export interface BookingRange {
  start: Date
  end: Date
  endsNextDay: boolean
}

/** Past this, a rolled-over end is a same-day typo (10:00 → 09:00), not a late meeting. */
const OVERNIGHT_TYPO_HOURS = 12

function atLocalTime(date: string, time: string, dayOffset = 0): Date {
  const [y, mo, d] = date.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  // Component constructor, not `${date}T${time}` + 24h, so a DST change overnight can't shift the time.
  return new Date(y, mo - 1, d + dayOffset, h, mi)
}

/** Null when the date or a time is missing or malformed. */
export function resolveBookingRange(date: string, startTime: string, endTime: string): BookingRange | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) {
    return null
  }
  const start = atLocalTime(date, startTime)
  let end = atLocalTime(date, endTime)
  const endsNextDay = end.getTime() <= start.getTime()
  if (endsNextDay) end = atLocalTime(date, endTime, 1)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null
  return { start, end, endsNextDay }
}

/** Duration problems with a resolved range, or null if it's bookable. */
export function bookingDurationError({ start, end, endsNextDay }: BookingRange): string | null {
  const durationMin = (end.getTime() - start.getTime()) / 60_000
  if (endsNextDay && durationMin > OVERNIGHT_TYPO_HOURS * 60) return 'End time must be after start time'
  if (durationMin < MIN_BOOKING_DURATION_MINUTES) return `Minimum duration is ${MIN_BOOKING_DURATION_MINUTES} minutes`
  if (durationMin > MAX_BOOKING_DURATION_HOURS * 60) return `Maximum duration is ${MAX_BOOKING_DURATION_HOURS} hours`
  return null
}

function isNextLocalDay(start: Date, end: Date): boolean {
  return (
    end.getFullYear() !== start.getFullYear() ||
    end.getMonth() !== start.getMonth() ||
    end.getDate() !== start.getDate()
  )
}

/** "Ends next day (Wed, 1 Oct)" under the End field. */
export function endsNextDayHint(end: Date): string {
  return `Ends next day (${end.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })})`
}

/** "23:30 – 00:30 (+1 day) CEST" — the marker keeps overnight meetings from reading as backwards. */
export function formatBookingTimeRange(startIso: string, endIso: string, tzAbbr = ''): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  const nextDay = isNextLocalDay(start, end) ? ' (+1 day)' : ''
  return `${start.toLocaleTimeString(undefined, opts)} – ${end.toLocaleTimeString(undefined, opts)}${nextDay}${tzAbbr ? ` ${tzAbbr}` : ''}`
}

/**
 * A reminder is only offered if it can still fire before the meeting — one whose time has already
 * passed would arrive right after the invite and just duplicate it. "No reminder" (0) always fits.
 */
export function isRemindBeforeAvailable(minutes: number, minutesUntilStart: number): boolean {
  return minutes === 0 || minutes < minutesUntilStart
}

/** Keep the chosen reminder if it still fits, else the largest one that does (or "No reminder"). */
export function fitRemindBefore(current: number, minutesUntilStart: number): number {
  if (isRemindBeforeAvailable(current, minutesUntilStart)) return current
  const fitting = REMIND_BEFORE_OPTIONS.filter((m) => isRemindBeforeAvailable(m, minutesUntilStart))
  return Math.max(...fitting)
}

export function remindBeforeLabel(minutes: number): string {
  return minutes === 0 ? 'No reminder' : `${minutes} minutes`
}
